import { FloatingPortal, FloatingPortalProps } from '@floating-ui/react';

import { usePortalRoot } from '../context/PortalRootContext';

/**
 * Smart FloatingPortal that automatically uses the portal root from context.
 *
 * When rendered inside a modal (which provides PortalRootProvider), this component
 * will portal into the modal's DOM instead of document.body. This ensures proper
 * z-index handling since the dropdown stays in the same stacking context as the modal.
 *
 * When rendered outside a modal, it falls back to document.body (default behavior).
 *
 * Usage: Drop-in replacement for FloatingPortal
 * ```tsx
 * <SmartFloatingPortal>
 *   <Dropdown />
 * </SmartFloatingPortal>
 * ```
 */
export function SmartFloatingPortal({ children, root, ...props }: FloatingPortalProps) {
  const contextRoot = usePortalRoot();

  // Priority: explicit root prop > context root > undefined (body)
  const portalRoot = root ?? contextRoot ?? undefined;

  return (
    <FloatingPortal root={portalRoot} {...props}>
      {children}
    </FloatingPortal>
  );
}
